<template>
    <transition name="modal">
            <!-- Main flyout panel -->
            <div class="rhs-flyout-panel" :style="{ height: isEmbedded ? 'calc(100vh - 40px)' : 'calc(100vh - 86px)' }">
                <!-- Enhanced Header -->
                <header class="rhs-flyout-header">
                    <div class="rhs-flyout-header-slot">
                        <slot name="header">
                            <div class="rhs-flyout-header-content">
                                <h2 v-if="title" class="rhs-flyout-title">{{ title }}</h2>
                                <h2 v-else class="rhs-flyout-title">Panel</h2>
                            </div>
                        </slot>
                    </div>
                    <!-- Secondary actions area -->
                            <div class="rhs-flyout-header-actions">
                                <slot name="headerActions"></slot>
                                
                                <!-- Close button -->
                                <button 
                                    class="rhs-flyout-close-btn" 
                                    @click="closePanel"
                                    :title="closeTooltip"
                                >
                                    <img src="../../../public/icons/workflow/close.svg" class="theme-svg" alt="Close" />
                                </button>
                            </div>
                </header>

                <!-- Scrollable Body -->
                <main class="rhs-flyout-body">
                    <slot name="body">
                              <p>Default body content</p>
                    </slot>
                </main>

                <!-- Footer with action buttons -->
                <footer class="rhs-flyout-footer">
                    <slot name="footer" :saveDisabled="saveDisabled">
                        <div class="rhs-flyout-footer-actions">
                            <CommonButton @click="$emit('close')" 
                            :type="ButtonType.Primary" 
                            :text="localizer.Localize('Modal_OK')"
                            />
                        </div>
                    </slot>
                </footer>

                <!-- Additional modal/popup content -->
                <slot name="modal"></slot>
            </div>
    </transition>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Localizer } from '../../api/Localizer';
import  CommonButton  from '../helpers/CommonButton.vue';
import { ButtonType } from "../helpers/HelpersInterface";
import { isEmbedded } from '@/utils/Utils';

/**
 * @deprecated Use `RightPanelV3.vue` instead.
 */
export default defineComponent({
    name: 'RightPanelV2',
    components: {
            CommonButton
        },
    props: {
        // Title and content props
        title: {
            type: String,
            default: ''
        },
        saveDisabled: {
            type: Boolean,
            default: false
        },
        
        closeTooltip: {
            type: String,
            default: 'Close'
        },
        
        // Behavior props
        closeOnEscape: {
            type: Boolean,
            default: true
        },
        preventClose: {
            type: Boolean,
            default: false
        }
    },

    emits: ['close', 'save', 'closeAttempt', 'open', 'OnCloseClick'],

    data() {
        return {
            localizer: new Localizer(this.$store),
            isVisible: false,
            ButtonType,
            isEmbedded: isEmbedded()
        };
    },

    async mounted() {
        this.isVisible = true;
        
        if (this.closeOnEscape) {
            document.addEventListener('keydown', this.handleKeydown);
        }
        
        // Prevent body scroll when panel is open
        document.body.style.overflow = 'hidden';
        
        this.$emit('open');
    },

    beforeUnmount() {
        document.removeEventListener('keydown', this.handleKeydown);
        document.body.style.overflow = '';
    },

    methods: {
        handleKeydown(event: KeyboardEvent) {
            if (event.key === 'Escape' && this.closeOnEscape) {
                this.closePanel();
            }
        },

        closePanel() {
            if (this.preventClose) {
                this.$emit('closeAttempt');
                return;
            }
            
            this.isVisible = false;
            this.$emit('close');
        }
    }
});
</script>

<style scoped>
/* ===========================================
   RHS FLYOUT COMPONENT - MAIN STYLES
   =========================================== */
.rhs-flyout-panel {
    position: fixed;
    bottom: 0;
    right: 0;
    display: flex;
    flex-direction: column;
    background-color: #fff;
    z-index: 500;
    min-width: 475px;
    max-width: 475px;
    overflow: hidden;
    border-left: 1px solid var(--thin-border);
}

/* HEADER SECTION */

.rhs-flyout-header {
    flex-shrink: 0;
    background-color: #fff;
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    padding: 18px 18px 0px 18px;
}

.rhs-flyout-header :deep(.title) {
    font-size: 22px;
    font-weight: bold;
}

.rhs-flyout-header-slot {
    flex: 1;
    min-width: 0;
}

.rhs-flyout-header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 16px;
}

.rhs-flyout-title {
    font-size: 22px;
    font-weight: 600;
    color: var(--foreground-primary);
    margin: 0;
    line-height: 1.3;
    flex: 1;
}

.rhs-flyout-header-actions {
    display: flex;
    align-items: center;
    gap: 12px;
    flex-shrink: 0;
    float: right;
    margin: -20px -12px;
    cursor: pointer;
}

.rhs-flyout-close-btn {
    background: none;
    border: none;
    cursor: pointer;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.2s ease;
}


/* SCROLLABLE BODY SECTION */

.rhs-flyout-body {
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
    padding: 18px 18px;
}

.rhs-flyout-body .body {
            display: grid;
            grid-template-columns: 1fr;
            grid-row-gap: 21px;
            justify-items: stretch;
            -webkit-box-align: start;
            -ms-flex-align: start;
            /* align-items: start; */
        }
.rhs-flyout-body .body :deep(.bodyContents) {
            display: grid;
            grid-template-columns: auto 1fr;
            grid-column-gap: 21px;
            grid-row-gap: 21px;
            justify-items: stretch;
            -webkit-box-align: baseline;
            -ms-flex-align: baseline;
            padding-right: 12px;
        }
.rhs-flyout-body :deep(.bodyContents) {
            display: grid;
            grid-template-columns: auto 1fr;
            grid-column-gap: 21px;
            grid-row-gap: 21px;
            justify-items: stretch;
            -webkit-box-align: baseline;
            -ms-flex-align: baseline;
            align-items: unset;
            padding-right: 12px;
        }
        .rhs-flyout-body :deep(label) {
            font-weight: bold;
        }



/* NON-SCROLLING FOOTER SECTION */

.rhs-flyout-footer {
    display: flex;
    flex-shrink: 0;
    background-color: #fff;
    border-top: 1px solid var(--thin-border);
    justify-content: flex-end;
    padding: 16px;
    flex-direction: column;
}

.rhs-flyout-footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 16px;
    align-items: center;
}

/* Enhanced button styling to match existing system */
.rhs-flyout-footer :deep(.btnViewer) {
    border-radius: unset;
}

.rhs-flyout-footer :deep(.btnViewer:hover) {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.rhs-flyout-footer :deep(.btnViewer:active) {
    transform: translateY(0);
}

.rhs-flyout-footer :deep(.panelButtons) {
            display: flex;
            justify-content: flex-end;
            gap: 16px;
        }
/* ===========================================
   RESPONSIVE DESIGN
   =========================================== */

@media (max-width: 768px) {
    .rhs-flyout-panel {
        width: 100vw;
        max-width: none;
        border-left: none;
    }
    
    .rhs-flyout-header,
    .rhs-flyout-footer {
        padding-left: 16px;
        padding-right: 16px;
    }
    
    .rhs-flyout-title {
        font-size: 18px;
    }
}

@media (min-width: 1200px) {
    .rhs-flyout-panel {
        max-width: 550px;
    }
}

/* ===========================================
   ACCESSIBILITY IMPROVEMENTS
   =========================================== */

.rhs-flyout-close-btn:focus-visible {
    outline: 2px solid var(--accent-color);
    outline-offset: 2px;
}

/* High contrast mode support */
@media (prefers-contrast: high) {
    .rhs-flyout-panel {
        border-left: 2px solid;
    }
    
    .rhs-flyout-header {
        border-bottom: 2px solid;
    }
    
    .rhs-flyout-footer {
        border-top: 2px solid;
    }
}

</style>
